import { type ReactNode, useEffect, useId, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  XAxis,
  YAxis,
} from "recharts";

import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "~/components/ui/chart";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";
import { cn } from "~/lib/utils";
import { ActuatorInterval, ActuatorKind, FormattedChartData } from "./types";

export const STAGE_BAND_STYLES: Record<
  ActuatorKind,
  { label: string; fill: string }
> = {
  soilHeater: { label: "Soil heater", fill: "#f97316" },
  saltHeater: { label: "Salt heater", fill: "#dc2626" },
  saltPump0: { label: "Salt pump 0", fill: "#16a34a" },
  saltPump1: { label: "Salt pump 1", fill: "#0ea5e9" },
};

export const COLOR_PALETTE = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#9333ea",
  "#0ea5e9",
  "#ef4444",
  "#22c55e",
  "#8b5cf6",
  "#facc15",
  "#14b8a6",
  "#d946ef",
  "#fb7185",
];

export type PreparedLineChart = {
  data: Array<Record<string, number | null | string>>;
  series: Array<{ key: string; label: string; color: string }>;
  min?: number;
  max?: number;
};

export const prepareLineChart = (
  lines: FormattedChartData[],
  { skipNonPositive = false }: { skipNonPositive?: boolean } = {}
): PreparedLineChart => {
  if (!lines.length) {
    return { data: [], series: [] };
  }

  const seriesKeys = lines.map((_, index) => `series${index}`);
  const maxLength = Math.max(...lines.map((line) => line.data.length));
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;

  const data = Array.from({ length: maxLength }, (_, index) => {
    const entry: Record<string, number | null> = {};
    let sampleIndex: number | null = null;

    lines.forEach((line, lineIndex) => {
      const point = line.data[index];
      const key = seriesKeys[lineIndex];

      if (point) {
        sampleIndex = sampleIndex ?? point.x;
        const numericValue = Number(point.y);
        const isValid =
          Number.isFinite(numericValue) &&
          (!skipNonPositive || numericValue > 0);

        if (isValid) {
          entry[key] = numericValue;
          minValue = Math.min(minValue, numericValue);
          maxValue = Math.max(maxValue, numericValue);
        } else {
          entry[key] = null;
        }
      } else {
        entry[key] = null;
      }
    });

    return {
      sampleIndex: sampleIndex ?? index,
      ...entry,
    };
  });

  const filteredData = data.filter((row) =>
    seriesKeys.some((key) => row[key] !== null && row[key] !== undefined)
  );

  const min = minValue === Number.POSITIVE_INFINITY ? undefined : minValue;
  const max = maxValue === Number.NEGATIVE_INFINITY ? undefined : maxValue;

  return {
    data: filteredData,
    series: lines.map((line, index) => ({
      key: seriesKeys[index],
      label: line.id,
      color: line.color ?? COLOR_PALETTE[index % COLOR_PALETTE.length],
    })),
    min,
    max,
  };
};

export interface LineChartSectionProps {
  title?: ReactNode;
  lines: FormattedChartData[];
  yScale?: "linear" | "log";
  skipNonPositive?: boolean;
  yTickFormatter?: (value: number) => string;
  tooltipFormatter?: (value: number, name: string) => string;
  showLegend?: boolean;
  yAxisWidth?: number;
  className?: string;
  lineType?:
    | "linear"
    | "monotone"
    | "monotoneX"
    | "monotoneY"
    | "natural"
    | "step"
    | "stepAfter"
    | "stepBefore";
  onHoverIndexChange?: (sampleIndex: number | null) => void;
  /** Actuator on-periods to shade behind the lines (TRO stages). */
  stageBands?: ActuatorInterval[];
  /** Whether the stage bands start visible. Defaults to true. */
  defaultShowStageBands?: boolean;
}

export const LineChartSection = ({
  title,
  lines,
  yScale = "linear",
  skipNonPositive,
  yTickFormatter,
  tooltipFormatter,
  showLegend,
  yAxisWidth = 72,
  className,
  lineType = "linear",
  onHoverIndexChange,
  stageBands,
  defaultShowStageBands = true,
}: LineChartSectionProps) => {
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);
  const [showStageBands, setShowStageBands] = useState(defaultShowStageBands);
  const stageBandsToggleId = useId();
  const [selectionRange, setSelectionRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const { chart, activeKeys } = (() => {
    const prepared = prepareLineChart(lines, { skipNonPositive });
    const visibleKeys = new Set(
      prepared.series
        .map((serie) => serie.key)
        .filter((key) => !hiddenKeys.has(key))
    );
    const filteredData = prepared.data.map((row) => {
      const entries = Object.entries(row).reduce<Record<string, unknown>>(
        (acc, [key, value]) => {
          if (visibleKeys.has(key) || key === "sampleIndex") {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );
      return entries;
    });
    return {
      chart: {
        ...prepared,
        data: filteredData,
      },
      activeKeys: visibleKeys,
    };
  })();

  if (!chart.series.length || !chart.data.length) {
    return null;
  }

  useEffect(() => {
    setHiddenKeys((prev) => {
      const availableKeys = new Set(chart.series.map((serie) => serie.key));
      let mutated = false;
      const next = new Set<string>();
      prev.forEach((key) => {
        if (availableKeys.has(key)) {
          next.add(key);
        } else {
          mutated = true;
        }
      });
      return mutated ? next : prev;
    });
  }, [chart.series]);

  const dataWithinDomain = useMemo(() => {
    if (!zoomDomain) {
      return chart.data;
    }

    const [from, to] = zoomDomain;
    const min = Math.min(from, to);
    const max = Math.max(from, to);

    return chart.data.filter((row) => {
      const value = row.sampleIndex;
      return typeof value === "number" && value >= min && value <= max;
    });
  }, [chart.data, zoomDomain]);

  const domainData = dataWithinDomain.length ? dataWithinDomain : chart.data;

  const effectiveMin = (() => {
    if (!domainData.length) {
      return chart.min;
    }
    if (!activeKeys.size) {
      return chart.min;
    }
    const values: number[] = [];
    domainData.forEach((row) => {
      activeKeys.forEach((key) => {
        const value = row[key];
        if (typeof value === "number" && Number.isFinite(value)) {
          values.push(value);
        }
      });
    });
    if (!values.length) {
      return chart.min;
    }
    return Math.min(...values);
  })();

  const effectiveMax = (() => {
    if (!domainData.length) {
      return chart.max;
    }
    if (!activeKeys.size) {
      return chart.max;
    }
    const values: number[] = [];
    domainData.forEach((row) => {
      activeKeys.forEach((key) => {
        const value = row[key];
        if (typeof value === "number" && Number.isFinite(value)) {
          values.push(value);
        }
      });
    });
    if (!values.length) {
      return chart.max;
    }
    return Math.max(...values);
  })();

  const domainMin =
    effectiveMin === undefined
      ? "auto"
      : yScale === "log"
        ? Math.max(effectiveMin * 0.9, 1e-6)
        : effectiveMin - Math.abs(effectiveMin) * 0.1;
  const domainMax =
    effectiveMax === undefined
      ? "auto"
      : yScale === "log"
        ? effectiveMax * 1.1
        : effectiveMax + Math.abs(effectiveMax) * 0.1;

  const legendEnabled = showLegend ?? chart.series.length > 1;

  const chartConfig = chart.series.reduce<
    Record<string, { label: string; color: string }>
  >((acc, serie) => {
    acc[serie.key] = {
      label: serie.label,
      color: serie.color,
    };
    return acc;
  }, {});

  useEffect(() => {
    setHiddenKeys((prev) => {
      const activeKeys = new Set(chart.series.map((serie) => serie.key));
      const filteredKeys = Array.from(prev).filter((key) =>
        activeKeys.has(key)
      );
      if (filteredKeys.length === prev.size) {
        return prev;
      }
      return new Set(filteredKeys);
    });
  }, [chart.series]);

  useEffect(() => {
    if (!zoomDomain) {
      return;
    }

    const [from, to] = zoomDomain;
    const hasData = chart.data.some((row) => {
      const sampleIndex = row.sampleIndex;
      if (
        typeof sampleIndex !== "number" ||
        sampleIndex < Math.min(from, to) ||
        sampleIndex > Math.max(from, to)
      ) {
        return false;
      }

      for (const key of activeKeys) {
        const value = row[key];
        if (typeof value === "number" && Number.isFinite(value)) {
          return true;
        }
      }

      return false;
    });

    if (!hasData) {
      setZoomDomain(null);
    }
  }, [chart.data, zoomDomain, activeKeys]);

  const toggleSerie = (key: string) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const getSeriesLabel = (dataKey?: unknown) => {
    if (typeof dataKey !== "string") {
      return undefined;
    }
    return chart.series.find((serie) => serie.key === dataKey)?.label;
  };

  const handleMouseDown = (state: unknown) => {
    if (
      typeof state === "object" &&
      state !== null &&
      "activeLabel" in state &&
      typeof (state as { activeLabel?: unknown }).activeLabel === "number"
    ) {
      const label = (state as { activeLabel: number }).activeLabel;
      setSelectionRange({ start: label, end: label });
    }
  };

  const handleMouseMove = (state: unknown) => {
    if (onHoverIndexChange) {
      if (
        typeof state === "object" &&
        state !== null &&
        "activeLabel" in state &&
        typeof (state as { activeLabel?: unknown }).activeLabel === "number"
      ) {
        onHoverIndexChange((state as { activeLabel: number }).activeLabel);
      } else {
        onHoverIndexChange(null);
      }
    }

    if (!selectionRange) {
      return;
    }
    if (
      typeof state === "object" &&
      state !== null &&
      "activeLabel" in state &&
      typeof (state as { activeLabel?: unknown }).activeLabel === "number"
    ) {
      const label = (state as { activeLabel: number }).activeLabel;
      setSelectionRange((current) =>
        current ? { ...current, end: label } : current
      );
    }
  };

  const handleMouseUp = () => {
    if (!selectionRange) {
      return;
    }

    const { start, end } = selectionRange;
    setSelectionRange(null);

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return;
    }

    const [from, to] = start <= end ? [start, end] : [end, start];

    if (
      !Number.isFinite(from) ||
      !Number.isFinite(to) ||
      Math.abs(to - from) < 1
    ) {
      return;
    }

    const hasDataInRange = chart.data.some((row) => {
      const sampleIndex = row.sampleIndex;
      if (
        typeof sampleIndex !== "number" ||
        sampleIndex < from ||
        sampleIndex > to
      ) {
        return false;
      }

      for (const key of activeKeys) {
        const value = row[key];
        if (typeof value === "number" && Number.isFinite(value)) {
          return true;
        }
      }

      return false;
    });

    if (!hasDataInRange) {
      return;
    }

    setZoomDomain([from, to]);
  };

  const handleMouseLeave = () => {
    setSelectionRange(null);
    onHoverIndexChange?.(null);
  };

  const handleResetZoom = () => {
    setZoomDomain(null);
  };

  const selectionBounds = selectionRange
    ? [
        Math.min(selectionRange.start, selectionRange.end),
        Math.max(selectionRange.start, selectionRange.end),
      ]
    : null;

  const xDomain: [number | "dataMin", number | "dataMax"] = zoomDomain
    ? [zoomDomain[0], zoomDomain[1]]
    : ["dataMin", "dataMax"];

  const hasStageBands = Boolean(stageBands?.length);
  const visibleStageBands = hasStageBands && showStageBands ? stageBands! : [];
  const stageBandKinds = Array.from(
    new Set(visibleStageBands.map((band) => band.kind))
  );

  const showHeader = Boolean(title) || Boolean(zoomDomain) || hasStageBands;

  return (
    <div className={className ?? "space-y-3"}>
      {showHeader ? (
        <div className="flex items-center justify-between gap-2">
          {title ? title : <div className="flex-1" />}
          <div className="flex items-center gap-4">
            {hasStageBands ? (
              <div className="flex items-center gap-3">
                {stageBandKinds.map((kind) => (
                  <span
                    key={kind}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{
                        backgroundColor: STAGE_BAND_STYLES[kind].fill,
                        opacity: 0.5,
                      }}
                    />
                    {STAGE_BAND_STYLES[kind].label}
                  </span>
                ))}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={stageBandsToggleId}
                    checked={showStageBands}
                    onCheckedChange={(checked) =>
                      setShowStageBands(checked === true)
                    }
                  />
                  <Label
                    htmlFor={stageBandsToggleId}
                    className="text-xs text-muted-foreground"
                  >
                    Stages
                  </Label>
                </div>
              </div>
            ) : null}
            {zoomDomain ? (
              <Button size="sm" variant="outline" onClick={handleResetZoom}>
                Reset Zoom
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-[340px] w-full"
      >
        <LineChart
          data={domainData}
          margin={{ top: 12, right: 12, bottom: 12, left: 12 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey="sampleIndex"
            type="number"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={xDomain}
          />
          <YAxis
            scale={yScale}
            tickLine={false}
            axisLine={false}
            width={yAxisWidth}
            allowDecimals
            allowDataOverflow={yScale === "log"}
            domain={[domainMin, domainMax]}
            tickFormatter={yTickFormatter}
          />
          {visibleStageBands.map((band, index) => (
            <ReferenceArea
              key={`stage-${band.kind}-${index}`}
              x1={band.from}
              x2={band.to}
              ifOverflow="hidden"
              fill={STAGE_BAND_STYLES[band.kind].fill}
              fillOpacity={0.12}
              stroke="none"
            />
          ))}
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(_, tooltipPayload) => {
                  const rawValue = tooltipPayload?.[0]?.payload?.sampleIndex;
                  const sample =
                    typeof rawValue === "number" ? rawValue : rawValue ?? "";
                  return `Index ${sample}`;
                }}
                formatter={(value, name, item) => {
                  if (typeof value !== "number") {
                    return value;
                  }
                  const formatted =
                    tooltipFormatter?.(value, String(name)) ??
                    value.toLocaleString(undefined, {
                      maximumFractionDigits: yScale === "log" ? 4 : 2,
                    });
                  const label =
                    (typeof item === "object" &&
                      item !== null &&
                      "dataKey" in item &&
                      getSeriesLabel(
                        typeof item.dataKey === "string"
                          ? item.dataKey
                          : undefined
                      )) ||
                    getSeriesLabel(
                      typeof name === "string" ? name : undefined
                    ) ||
                    (typeof name === "string" ? name : "Series");
                  const indicatorColor =
                    typeof item === "object" &&
                    item !== null &&
                    "color" in item &&
                    typeof item.color === "string"
                      ? item.color
                      : undefined;
                  return (
                    <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                      <span
                        className="text-muted-foreground"
                        style={
                          indicatorColor ? { color: indicatorColor } : undefined
                        }
                      >
                        {label}
                      </span>
                      <span className="font-medium text-foreground tabular-nums text-left">
                        {formatted}
                      </span>
                    </div>
                  );
                }}
              />
            }
          />
          {chart.series.map((serie) => (
            <Line
              key={serie.key}
              type={lineType}
              dataKey={serie.key}
              stroke={`var(--color-${serie.key})`}
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
              hide={hiddenKeys.has(serie.key)}
            />
          ))}
          {legendEnabled && (
            <ChartLegend
              verticalAlign="bottom"
              content={
                <LegendToggle
                  series={chart.series}
                  hiddenKeys={hiddenKeys}
                  onToggle={toggleSerie}
                />
              }
            />
          )}
          {selectionBounds ? (
            <ReferenceArea
              x1={selectionBounds[0]}
              x2={selectionBounds[1]}
              strokeOpacity={0.2}
              fillOpacity={0.1}
              fill="rgba(37, 99, 235, 0.12)"
              stroke="#2563eb"
            />
          ) : null}
        </LineChart>
      </ChartContainer>
    </div>
  );
};

type LegendToggleProps = {
  series: PreparedLineChart["series"];
  hiddenKeys: Set<string>;
  onToggle: (key: string) => void;
};

const LegendToggle = ({ series, hiddenKeys, onToggle }: LegendToggleProps) => {
  if (!series.length) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 pt-3 text-xs font-medium">
      {series.map((serie) => {
        const isHidden = hiddenKeys.has(serie.key);
        return (
          <button
            key={serie.key}
            type="button"
            onClick={(event) => {
              event.preventDefault();
              onToggle(serie.key);
            }}
            aria-pressed={!isHidden}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
              isHidden
                ? "border-transparent bg-muted text-muted-foreground"
                : "border-border bg-background text-foreground shadow-sm"
            )}
          >
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: `var(--color-${serie.key})` }}
              aria-hidden="true"
            />
            <span>{serie.label}</span>
          </button>
        );
      })}
    </div>
  );
};
